import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Add, Delete } from '@mui/icons-material';
import { GatewayAllocation, Gateway, ValidationResult } from '../../types/dynamicRouting';

interface GatewayDistributionEditorProps {
  distribution: GatewayAllocation[];
  gateways: Gateway[];
  onChange: (distribution: GatewayAllocation[]) => void;
  validationResult?: ValidationResult;
  distributionPath: string;
  allowEmpty?: boolean;
}

const GatewayDistributionEditor: React.FC<GatewayDistributionEditorProps> = ({
  distribution,
  gateways,
  onChange,
  validationResult,
  distributionPath,
  allowEmpty = false,
}) => { 
  const handleAddAllocation = () => {
    const newAllocation: GatewayAllocation = {
      gatewayId: gateways.length > 0 ? gateways[0].id : '',
      percentage: 0,
    };
    onChange([...distribution, newAllocation]);
  };

  const handleRemoveAllocation = (index: number) => {
    const newDistribution = distribution.filter((_, i) => i !== index);
    onChange(newDistribution);
  };

  const handleAllocationChange = (index: number, field: keyof GatewayAllocation, value: any) => {
    const newDistribution = [...distribution];
    newDistribution[index] = {
      ...newDistribution[index],
      [field]: value,
    };
    onChange(newDistribution);
  };

  const handleAutoBalance = () => {
    if (distribution.length === 0) return;
    
    const equalPercentage = Math.floor(100 / distribution.length);
    const remainder = 100 - (equalPercentage * distribution.length);
    
    const newDistribution = distribution.map((allocation, index) => ({
      ...allocation,
      percentage: index === 0 ? equalPercentage + remainder : equalPercentage,
    }));
    
    onChange(newDistribution);
  };

  // Helper function to get validation messages for distribution
  const getDistributionValidationMessages = () => {
    if (!validationResult) return [];
    return validationResult.messages.filter(msg => 
      msg.path.includes(distributionPath) || msg.path.includes('gatewayDistribution')
    );
  };

  const totalPercentage = distribution.reduce((sum, allocation) => sum + (allocation.percentage || 0), 0);
  const isValidTotal = totalPercentage === 100;
  const validationMessages = getDistributionValidationMessages();
  const hasErrors = validationMessages.some(msg => msg.level === 'error');

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define how traffic should be distributed across gateways. Total percentage must equal 100%.
      </Typography>

      {/* Show validation messages */}
      {validationMessages.map((msg, index) => (
        <Alert key={index} severity={msg.level} sx={{ mb: 2 }}>
          <strong>[{msg.code}]</strong> {msg.message}
          {msg.suggestion && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              💡 {msg.suggestion}
            </Typography>
          )}
        </Alert>
      ))}

      {/* Total Percentage Indicator */}
      <Box sx={{ mb: 2, p: 2, bgcolor: isValidTotal ? 'success.light' : 'warning.light', borderRadius: 1 }}>
        <Typography variant="subtitle2">
          Total Percentage: {totalPercentage}% / 100%
        </Typography>
        {!isValidTotal && (
          <Typography variant="body2" color="error">
            ⚠️ Total must equal 100% for valid configuration
          </Typography>
        )}
      </Box>

      {/* Gateway Allocations */}
      {distribution.map((allocation, index) => {
        
        return (
          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{xs:12, md:4}}>
                <FormControl fullWidth>
                   <InputLabel>Gateway</InputLabel>
                  <InputLabel id={`gateway-label-${index}`}>Gateway</InputLabel>
                  <Select
                    label="Gateway"
                    labelId={`gateway-label-${index}`}  
                    value={allocation.gatewayId}
                    onChange={(e) => handleAllocationChange(index, 'gatewayId', e.target.value)}
                    error={hasErrors}
                  >
                    {gateways.map(gateway => (
                      <MenuItem key={gateway.id} value={gateway.id}>
                        {gateway.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{xs:12, md:3}}>
                <TextField
                  fullWidth
                  type="number"
                  label="Percentage"
                  value={allocation.percentage}
                  onChange={(e) => handleAllocationChange(index, 'percentage', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  helperText="0-100%"
                  error={hasErrors}
                />
              </Grid>
              
              <Grid size={{xs:12, md:2}}>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveAllocation(index)}
                  disabled={!allowEmpty && distribution.length === 1}
                >
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddAllocation}
          disabled={gateways.length === 0}
        >
          Add Gateway
        </Button>
        
        {distribution.length > 1 && (
          <Button
            variant="outlined"
            onClick={handleAutoBalance}
          >
            Auto Balance (Equal Distribution)
          </Button>
        )}
      </Box>

      {gateways.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No gateways available. Please add gateways first in the Gateways section.
        </Alert>
      )}

      {distribution.length === 0 && !allowEmpty && (
        <Alert severity="error" sx={{ mt: 2 }}>
          At least one gateway allocation is required.
        </Alert>
      )}
    </Box>
  );
};

export default GatewayDistributionEditor;
